import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { BookOpenCheck, Save, Loader2, Calendar, ChevronRight, ChevronLeft, Lock } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  STAGES, STAGE_KEYS, GRADES_BY_STAGE, sectionsForGrade,
  ACADEMIC_YEARS, CURRENT_ACADEMIC_YEAR,
  PLAN_DAYS, PLAN_PERIODS, weekStartOf, weekEndOf,
} from "../lib/constants";
import { saveLessonPlan, getLessonPlan, listLessonPlans } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

function makeEmptyCells() {
  const c = {};
  PLAN_DAYS.forEach(d => PLAN_PERIODS.forEach(p => { c[`${d.key}_${p.key}`] = { lesson: "", homework: "" }; }));
  return c;
}

export default function LessonPlanForm() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { id } = useParams();
  const [sp] = useSearchParams();

  const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  const today = new Date().toISOString().slice(0, 10);
  const [planId, setPlanId] = useState(id || null);
  const [weekStart, setWeekStart] = useState(sp.get("week") || weekStartOf(today));
  const [stage, setStage] = useState("");
  const [grade, setGrade] = useState("");
  const [section, setSection] = useState("");
  const [year, setYear] = useState(CURRENT_ACADEMIC_YEAR);
  const [cells, setCells] = useState(makeEmptyCells());
  const [saving, setSaving] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [copyDialog, setCopyDialog] = useState(null); // { day, period, lesson, homework }
  const [copyTargetSection, setCopyTargetSection] = useState("");
  const [copyTargetDay, setCopyTargetDay] = useState("");
  const [copyTargetPeriod, setCopyTargetPeriod] = useState("");
  const [copying, setCopying] = useState(false);

  const weekEnd = useMemo(() => weekEndOf(weekStart), [weekStart]);

  const allowedStages = user?.role === "admin" || user?.role === "super_admin" || !user?.assigned_stages?.length
    ? STAGE_KEYS : STAGE_KEYS.filter(s => user.assigned_stages.includes(s));
  const grades = stage ? GRADES_BY_STAGE[stage] : [];
  const sections = sectionsForGrade(grade);

  const loadPlanData = useCallback((p) => {
    setPlanId(p.id);
    setWeekStart(p.week_start);
    setStage(p.stage);
    setGrade(p.grade);
    setSection(p.section);
    setYear(p.academic_year);
    setCells({ ...makeEmptyCells(), ...(p.cells || {}) });
  }, []);

  // تحميل خطة موجودة عبر رابط تعديل مباشر (من صفحة القائمة)
  useEffect(() => {
    if (!id) return;
    getLessonPlan(id).then(loadPlanData).catch(() => toast.error("تعذر تحميل الخطة"));
  }, [id, loadPlanData]);

  // عند اختيار أسبوع/صف/فصل جديد (بدون رابط تعديل مباشر): جلب الخطة المشتركة الحالية إن وجدت،
  // حتى يرى المعلم إدخالات زملائه ويكمل عليها بدل إنشاء خطة مكررة
  useEffect(() => {
    if (id || !stage || !grade || !section || !weekStart) return;
    let cancelled = false;
    setLoadingExisting(true);
    listLessonPlans({ grade, section, week_from: weekStart, week_to: weekStart })
      .then((rows) => {
        if (cancelled) return;
        const match = rows.find(r => r.week_start === weekStart && r.grade === grade && r.section === section);
        if (match) {
          loadPlanData(match);
        } else {
          setPlanId(null);
          setCells(makeEmptyCells());
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingExisting(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, stage, grade, section, weekStart]);

  const setCell = (day, period, field, value) => {
    const k = `${day}_${period}`;
    setCells(prev => ({ ...prev, [k]: { ...(prev[k] || { lesson: "", homework: "" }), [field]: value } }));
  };

  const shiftWeek = (delta) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + delta * 7);
    setWeekStart(d.toISOString().slice(0, 10));
  };

  const isCellLocked = (cell) => {
    if (!cell?.teacher_id) return false; // خلية فارغة أو بلا مالك: متاحة للجميع
    if (isAdmin) return false; // الإدارة تعدل أي خلية
    return cell.teacher_id !== user?.id; // مملوكة لمعلم آخر
  };

  // فتح نافذة "نسخ لفصل آخر" لخلية معينة (نفس الصف، فصل/يوم/حصة يختارها المعلم)
  const openCopyDialog = (day, period, cell) => {
    setCopyDialog({ day, period, lesson: cell.lesson || "", homework: cell.homework || "" });
    setCopyTargetSection("");
    setCopyTargetDay(day);
    setCopyTargetPeriod(period);
  };

  const confirmCopy = async () => {
    if (!copyTargetSection) {
      toast.error("اختر الفصل الهدف");
      return;
    }
    setCopying(true);
    try {
      // نجيب الخطة الحالية لنفس الصف والفصل الهدف (لو موجودة) عشان ندمج فيها الخلية الجديدة فقط
      const rows = await listLessonPlans({ grade, section: copyTargetSection, week_from: weekStart, week_to: weekStart });
      const existing = rows.find(r => r.week_start === weekStart && r.grade === grade && r.section === copyTargetSection);
      const targetKey = `${copyTargetDay}_${copyTargetPeriod}`;
      const targetCell = existing?.cells?.[targetKey];

      if (targetCell?.teacher_id && targetCell.teacher_id !== user?.id && !isAdmin) {
        toast.error("هذه الخلية في الفصل الهدف مسجّلة بالفعل بواسطة معلم آخر");
        setCopying(false);
        return;
      }

      await saveLessonPlan({
        week_start: weekStart,
        week_end: weekEnd,
        stage, grade, section: copyTargetSection,
        academic_year: year,
        cells: { [targetKey]: { lesson: copyDialog.lesson, homework: copyDialog.homework } },
      });
      toast.success(`تم النسخ إلى فصل ${copyTargetSection} بنجاح`);
      setCopyDialog(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر النسخ");
    } finally {
      setCopying(false);
    }
  };

  const onSave = async () => {
    if (!stage || !grade || !section) {
      toast.error("اختر المرحلة والصف والفصل");
      return;
    }
    setSaving(true);
    try {
      // نرسل فقط الخلايا التي يملكها المستخدم الحالي (أو فارغة أو الإدارة) لتفادي أي محاولة كتابة على خلايا غيره
      const cellsToSend = {};
      Object.entries(cells).forEach(([k, v]) => {
        if (!isCellLocked(v)) {
          cellsToSend[k] = { lesson: v.lesson || "", homework: v.homework || "" };
        }
      });
      const saved = await saveLessonPlan({
        week_start: weekStart,
        week_end: weekEnd,
        stage, grade, section,
        academic_year: year,
        cells: cellsToSend,
      });
      toast.success("تم حفظ الخطة بنجاح");
      loadPlanData(saved);
      nav("/lesson-plans");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر الحفظ");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader title={planId ? "تعديل خطة أسبوعية" : "إنشاء / إكمال خطة أسبوعية"} subtitle="جدول 5 أيام × 7 حصص — خطة مشتركة لكل معلمي الفصل، كل حصة تُحفظ باسم من أدخلها" icon={BookOpenCheck} />

      <Card className="p-4 sm:p-5 border-slate-200 mb-5">
        {/* Week selector */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Button variant="outline" size="sm" onClick={() => shiftWeek(-1)} className="h-9 gap-1" data-testid="btn-prev-week">
            <ChevronRight size={15} /> الأسبوع السابق
          </Button>
          <div className="flex-1 min-w-[200px] flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-teal-50 border border-teal-200 text-teal-800">
            <Calendar size={16} />
            <span className="font-cairo font-bold text-sm tabular-nums" dir="ltr">{weekStart} → {weekEnd}</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => shiftWeek(1)} className="h-9 gap-1" data-testid="btn-next-week">
            الأسبوع التالي <ChevronLeft size={15} />
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="space-y-1.5">
            <Label>بداية الأسبوع</Label>
            <Input type="date" value={weekStart} onChange={(e) => setWeekStart(e.target.value)} className="h-10" data-testid="lp-week-start" />
          </div>
          <div className="space-y-1.5">
            <Label>المرحلة</Label>
            <Select value={stage} onValueChange={(v) => { setStage(v); setGrade(""); setSection(""); }}>
              <SelectTrigger className="h-10" dir="rtl" data-testid="lp-stage"><SelectValue placeholder="اختر" /></SelectTrigger>
              <SelectContent dir="rtl">{allowedStages.map(k => <SelectItem key={k} value={k}>{STAGES[k]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>الصف</Label>
            <Select value={grade} onValueChange={(v) => { setGrade(v); setSection(""); }} disabled={!stage}>
              <SelectTrigger className="h-10" dir="rtl" data-testid="lp-grade"><SelectValue placeholder={stage ? "اختر" : "..."} /></SelectTrigger>
              <SelectContent dir="rtl">{grades.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>الفصل</Label>
            <Select value={section} onValueChange={setSection} disabled={!grade}>
              <SelectTrigger className="h-10" dir="rtl" data-testid="lp-section"><SelectValue placeholder={grade ? "اختر" : "..."} /></SelectTrigger>
              <SelectContent dir="rtl">{sections.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label>العام الدراسي</Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="h-10" dir="rtl"><SelectValue /></SelectTrigger>
              <SelectContent dir="rtl">{ACADEMIC_YEARS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        {loadingExisting && (
          <div className="mt-3 text-xs text-slate-500 flex items-center gap-2">
            <Loader2 className="animate-spin" size={14} /> جاري تحميل الخطة الحالية لهذا الفصل إن وُجدت...
          </div>
        )}
      </Card>

      {/* Grid */}
      <Card className="border-slate-200 overflow-hidden mb-5">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="lp-grid">
            <thead>
              <tr className="bg-teal-700 text-white">
                <th className="px-3 py-3 text-center font-cairo font-semibold w-28">اليوم / الحصة</th>
                {PLAN_PERIODS.map(p => (
                  <th key={p.key} className="px-2 py-3 text-center font-cairo font-semibold">{p.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PLAN_DAYS.map((d, dIdx) => (
                <tr key={d.key} className={dIdx % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                  <td className="px-3 py-2 align-top text-center font-cairo font-bold text-teal-800 bg-teal-50/50">{d.label}</td>
                  {PLAN_PERIODS.map(p => {
                    const k = `${d.key}_${p.key}`;
                    const cell = cells[k] || { lesson: "", homework: "" };
                    const locked = isCellLocked(cell);
                    return (
                      <td key={p.key} className={`p-1.5 align-top border-r border-slate-100 ${locked ? "bg-slate-100/70" : ""}`}>
                        {locked && cell.teacher_name && (
                          <div className="flex items-center gap-1 text-[10px] text-slate-500 mb-1">
                            <Lock size={10} /> {cell.teacher_name}
                          </div>
                        )}
                        <Textarea
                          value={cell.lesson}
                          onChange={(e) => setCell(d.key, p.key, "lesson", e.target.value)}
                          placeholder="الدرس"
                          className="bg-white text-xs min-h-[44px] resize-none border-slate-200 mb-1 disabled:opacity-70 disabled:cursor-not-allowed"
                          dir="rtl"
                          rows={2}
                          disabled={locked}
                          data-testid={`lp-cell-${k}-lesson`}
                        />
                        <Textarea
                          value={cell.homework}
                          onChange={(e) => setCell(d.key, p.key, "homework", e.target.value)}
                          placeholder="الواجب"
                          className="bg-amber-50/30 text-xs min-h-[36px] resize-none border-amber-200/60 disabled:opacity-70 disabled:cursor-not-allowed"
                          dir="rtl"
                          rows={1}
                          disabled={locked}
                          data-testid={`lp-cell-${k}-homework`}
                        />
                        {!locked && cell.teacher_name && (
                          <div className="text-[10px] text-teal-700 mt-1">{cell.teacher_name}</div>
                        )}
                        {!locked && (cell.lesson || cell.homework) && (
                          <button
                            type="button"
                            onClick={() => openCopyDialog(d.key, p.key, cell)}
                            className="mt-1 w-full text-[10px] text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded px-1 py-0.5 flex items-center justify-center gap-1"
                            data-testid={`lp-copy-${d.key}_${p.key}`}
                          >
                            📋 نسخ لفصل آخر
                          </button>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => nav("/lesson-plans")} className="h-11" data-testid="btn-cancel-lp">إلغاء</Button>
        <Button onClick={onSave} disabled={saving} className="h-11 bg-teal-700 hover:bg-teal-800 text-white gap-2 btn-elevate px-6" data-testid="btn-save-lp">
          {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
          {saving ? "جاري الحفظ..." : "حفظ الخطة"}
        </Button>
      </div>

      {copyDialog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setCopyDialog(null)}>
          <div className="bg-white rounded-xl p-5 w-full max-w-sm" onClick={(e) => e.stopPropagation()} dir="rtl">
            <h3 className="font-cairo font-bold text-teal-800 mb-1">نسخ الحصة لفصل آخر</h3>
            <p className="text-xs text-slate-500 mb-4">سيتم نسخ نفس الدرس والواجب إلى الفصل والموقع الذي تحدده (بنفس الصف: {grade})</p>

            <div className="space-y-3">
              <div>
                <Label className="text-xs">الفصل الهدف</Label>
                <Select value={copyTargetSection} onValueChange={setCopyTargetSection}>
                  <SelectTrigger className="h-10" dir="rtl"><SelectValue placeholder="اختر الفصل" /></SelectTrigger>
                  <SelectContent dir="rtl">
                    {sections.filter(s => s !== section).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">اليوم</Label>
                  <Select value={copyTargetDay} onValueChange={setCopyTargetDay}>
                    <SelectTrigger className="h-10" dir="rtl"><SelectValue /></SelectTrigger>
                    <SelectContent dir="rtl">
                      {PLAN_DAYS.map(d => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">الحصة</Label>
                  <Select value={String(copyTargetPeriod)} onValueChange={(v) => setCopyTargetPeriod(Number(v))}>
                    <SelectTrigger className="h-10" dir="rtl"><SelectValue /></SelectTrigger>
                    <SelectContent dir="rtl">
                      {PLAN_PERIODS.map(p => <SelectItem key={p.key} value={String(p.key)}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-2.5 text-xs text-slate-600">
                <div><strong>الدرس:</strong> {copyDialog.lesson || "—"}</div>
                {copyDialog.homework && <div className="mt-1"><strong>الواجب:</strong> {copyDialog.homework}</div>}
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setCopyDialog(null)} className="h-10">إلغاء</Button>
              <Button onClick={confirmCopy} disabled={copying} className="h-10 bg-blue-600 hover:bg-blue-700 text-white gap-2">
                {copying ? <Loader2 className="animate-spin" size={15} /> : "نسخ"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
